import { joinWithY } from '@utils/i18nGrammar';
import { getTranslatedRoleLabel } from './roleHierarchy';

// Roles a Super Admin can choose to count as billable seats on a plan.
// Mirrors the backend SELECTABLE_SEAT_COUNTING_ROLES. SUPER_ADMIN is excluded
// (platform role, never an organization seat).
// `label` deliberately not part of this table — display text now comes from
// `enumLabels.role*` via `getTranslatedRoleLabel` (see the functions below), same
// non-component-file factory pattern `roleHierarchy.ts` already uses.
export interface SeatCountingRoleOption {
  id: string;
}

export const SEAT_COUNTING_ROLE_OPTIONS: SeatCountingRoleOption[] = [
  { id: 'ADMIN' },
  { id: 'OWNER' },
  { id: 'PROJECT_MANAGER' },
  { id: 'SUPERINTENDENT' },
  { id: 'CREW_LEAD' },
  { id: 'EMPLOYEE' },
  { id: 'REVIEWER' },
];

// Default applied to new plans: all "office" roles (everything except CREW_LEAD
// and EMPLOYEE). Mirrors the backend DEFAULT_OFFICE_SEAT_COUNTING_ROLES.
export const DEFAULT_OFFICE_SEAT_COUNTING_ROLES: string[] = [
  'ADMIN',
  'OWNER',
  'PROJECT_MANAGER',
  'SUPERINTENDENT',
];

// Sprint 31, develop run 2 (priority 3, role-name sweep). `tEnumLabels`/`unknownLabel` are the
// caller's already-fetched `useTranslation('enumLabels')`/`useTranslation('common').unknown` —
// this plain `.ts` file has no `useTranslation()` hook of its own.
type RoleLabelMap = Record<string, string>;

// Labels of roles that DO count as seats, in canonical order.
export const getSeatCountingRoleLabels = (
  seatCountingRoles: string[] = [],
  tEnumLabels: RoleLabelMap,
  unknownLabel: string,
): string[] =>
  SEAT_COUNTING_ROLE_OPTIONS.filter((r) => seatCountingRoles.includes(r.id)).map((r) =>
    getTranslatedRoleLabel(r.id, tEnumLabels, unknownLabel),
  );

// Labels of the selectable roles that do NOT count as seats, in canonical order.
export const getNonSeatCountingRoleLabels = (
  seatCountingRoles: string[] = [],
  tEnumLabels: RoleLabelMap,
  unknownLabel: string,
): string[] =>
  SEAT_COUNTING_ROLE_OPTIONS.filter((r) => !seatCountingRoles.includes(r.id)).map((r) =>
    getTranslatedRoleLabel(r.id, tEnumLabels, unknownLabel),
  );

/**
 * Sprint 31, story-158 (docs/ux/i18n-house-style.md §13.3). Mid-sentence lowercase role
 * name/singular-plural translations `buildFreeRolesNote`/`buildSeatWarning` need — a plain
 * `.ts` file has no `useTranslation()` hook, so callers pass the already-fetched
 * `useTranslation('billing').seats` section through. `PROJECT_MANAGER`/`CREW_LEAD` are the
 * phrase-internal case: the plural marker lands on the head noun ("Gerentes de proyecto"), not
 * the phrase's last word.
 */
export interface SeatCountingRoleTranslations {
  roleAdminSingular: string;
  roleAdminPlural: string;
  roleOwnerSingular: string;
  roleOwnerPlural: string;
  roleProjectManagerSingular: string;
  roleProjectManagerPlural: string;
  roleSuperintendentSingular: string;
  roleSuperintendentPlural: string;
  roleCrewLeadSingular: string;
  roleCrewLeadPlural: string;
  roleEmployeeSingular: string;
  roleEmployeePlural: string;
  roleReviewerSingular: string;
  roleReviewerPlural: string;
  noSeatNoteOne: string;
  noSeatNoteOther: string;
  roleListArticle: string;
  listConjunction: string;
  listConjunctionBeforeI: string;
}

function roleSingular(t: SeatCountingRoleTranslations, id: string): string {
  switch (id) {
    case 'ADMIN':
      return t.roleAdminSingular;
    case 'OWNER':
      return t.roleOwnerSingular;
    case 'PROJECT_MANAGER':
      return t.roleProjectManagerSingular;
    case 'SUPERINTENDENT':
      return t.roleSuperintendentSingular;
    case 'CREW_LEAD':
      return t.roleCrewLeadSingular;
    case 'EMPLOYEE':
      return t.roleEmployeeSingular;
    case 'REVIEWER':
      return t.roleReviewerSingular;
    default:
      return id;
  }
}

function rolePlural(t: SeatCountingRoleTranslations, id: string): string {
  switch (id) {
    case 'ADMIN':
      return t.roleAdminPlural;
    case 'OWNER':
      return t.roleOwnerPlural;
    case 'PROJECT_MANAGER':
      return t.roleProjectManagerPlural;
    case 'SUPERINTENDENT':
      return t.roleSuperintendentPlural;
    case 'CREW_LEAD':
      return t.roleCrewLeadPlural;
    case 'EMPLOYEE':
      return t.roleEmployeePlural;
    case 'REVIEWER':
      return t.roleReviewerPlural;
    default:
      return id;
  }
}

function capitalizeFirst(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

// Free-role IDs (selectable roles that do NOT count as seats), in canonical order.
const getFreeRoleIds = (seatCountingRoles?: string[]): string[] =>
  SEAT_COUNTING_ROLE_OPTIONS.filter((r) => !(seatCountingRoles ?? []).includes(r.id)).map(
    (r) => r.id,
  );

/**
 * Human sentence naming the roles that do NOT occupy a seat, e.g.
 * "Los líderes de cuadrilla y los empleados no usan un puesto." Returns null when there is no
 * plan data yet or when every role uses a seat.
 */
export const buildFreeRolesNote = (
  t: SeatCountingRoleTranslations,
  seatCountingRoles?: string[],
): string | null => {
  if (!seatCountingRoles) return null;
  const freeIds = getFreeRoleIds(seatCountingRoles);
  if (freeIds.length === 0) return null;
  if (freeIds.length === 1) {
    return t.noSeatNoteOne.replace('{role}', roleSingular(t, freeIds[0]));
  }
  // Each free role's plural phrase, given its article (Spanish: "los administradores"; English:
  // no article), joined by the shared conjunction helper — not string concatenation (§11.3).
  const roleList = joinWithY(
    freeIds.map((id) => `${t.roleListArticle}${rolePlural(t, id)}`),
    t.listConjunction,
    t.listConjunctionBeforeI,
  );
  return t.noSeatNoteOther.replace('{roleList}', capitalizeFirst(roleList));
};
